'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PLAN_MAX_FILE_BYTES, type AudioBatchLanguage } from '@kiao-voice/core'
import { Button, KiaoModal } from '@kiao-voice/ui-kit'
import { readStoredToken } from '@/features/auth/auth-storage'
import {
  UploadLockLabel,
  UploadProgressFooter,
  UploadProgressPanel,
} from '@/features/batch-transcription/upload-progress-panel'
import { useWorkspaceScopedLoader, type WorkspaceScopedLoadContext } from '@kiao-voice/hooks'
import {
  preflightAudioBatch,
  submitAudioBatch,
  submitAudioBatchLinks,
} from '@/features/batch-transcription/api-client'
import { parseSourceLinks } from '@/features/batch-transcription/source-links'
import { useUploadSelectionPreflight } from '@/features/batch-transcription/use-upload-selection-preflight'
import {
  restoreRememberedTemplate,
  writeRememberedTemplate,
} from '@/features/batch-transcription/remembered-template'
import { probeFileDurationSecs } from '@/features/anonymous-trial/api-client'
import { fetchNotesTemplates } from '@/features/meeting-notes/api-client'
import type { NotesTemplate } from '@/features/meeting-notes/types'
import {
  describeValidationError,
  formatFileSize,
  isVideoBatchFile,
  validateAdditions,
} from '@/features/batch-transcription/file-validation'
import {
  AUDIO_BATCH_MAX_FILES,
  type AudioBatchSubmitResponse,
} from '@/features/batch-transcription/types'
import {
  createDictionary as createDictionaryRequest,
  createDictionaryEntry as createDictionaryEntryRequest,
  getDictionary,
  listDictionaries,
} from '@/features/dictionary/api-client'
import type { HintStorageLike } from '@/features/dictionary/hint-suggestions'
import type { DictionaryEntryView } from '@/features/dictionary/types'
import { useRefreshOnRefocus } from '@/hooks/use-refresh-on-refocus'
import { useUploadDictionaries } from '@/features/batch-transcription/use-upload-dictionaries'
import { useHintTerms } from '@/features/batch-transcription/use-hint-terms'
import { SaveTermDialog, SAVE_TARGET_NEW } from '@/features/batch-transcription/save-term-dialog'
import {
  UploadSettingsBody,
  type UploadSourceMode,
} from '@/features/batch-transcription/upload-settings-body'
import {
  useUploadLifecycle,
  type SubmissionSnapshot,
} from '@/features/batch-transcription/use-upload-lifecycle'

// A missing authenticated probe must never turn into a larger client-side
// upload allowance. Free is the conservative public baseline; the API remains
// authoritative if a user has since upgraded. The server answers the same
// question with the Pro ceiling — deliberately the opposite fallback, because
// there a wrong guess locks a paying account out instead of sending it a file
// the API will refuse.
const UNKNOWN_PLAN_MAX_FILE_BYTES = PLAN_MAX_FILE_BYTES.free
/** Stable empty queue for the file-only hooks while the dialog is in link mode. */
const NO_FILES: File[] = []

interface UploadModalProps {
  /** Whether the modal is mounted/visible. */
  open: boolean
  /** Close request (Escape, backdrop, Cancel, X). The parent owns focus return. */
  onClose: () => void
  /** Fired once the batch is accepted by the server — the parent inserts the
   *  new "processing" rows + closes. The dialog stays open and shows upload
   *  progress until then: it used to close on the button press, which left a
   *  multi-minute upload with no representation anywhere on screen. */
  onSubmitted: (response: AudioBatchSubmitResponse) => void
  /** Test seam — defaults to the real `localStorage` read. */
  loadStoredToken?: () => string | null
  /** Test seam — defaults to the real upload call. */
  submit?: typeof submitAudioBatch
  /** Test seam — defaults to the real link-mode submit. */
  submitLinks?: typeof submitAudioBatchLinks
  /** Null disables the network preflight for focused test fixtures. */
  preflight?: typeof preflightAudioBatch | null
  /** The caller's per-file duration cap (minutes) from `/audio-batches/me`, or
   *  null for an uncapped plan / unknown. When set, queued files are probed
   *  client-side before upload and an over-cap file opens the focused limit dialog
   *  instead of spending an upload + worker ffprobe round trip (#173). */
  maxFileMinutes?: number | null
  /** The caller's per-file byte cap from `/audio-batches/me`. Null/unknown
   * falls back to the conservative Free cap; the API remains authoritative. */
  maxFileBytes?: number | null
  /** Effective plan returned by the same authenticated cap probe. */
  currentPlan?: string | null
  /** Test seam for the focused upgrade CTA. */
  navigateToPricing?: (url: string) => void
  /** Test seam — defaults to the real browser duration probe. */
  probeDuration?: typeof probeFileDurationSecs
  /** Whether the 「國語+台語」 upload language option is available (from the
   *  `/audio-batches/me` probe). Off by default; hides the language selector. */
  taigiEnabled?: boolean
  /** Test seam — defaults to the real 整理範本 list fetch. */
  fetchTemplates?: typeof fetchNotesTemplates
  /** Whether the caller is currently working inside a team workspace. */
  teamContext?: boolean
  /** Test seam — defaults to the real dictionary list fetch. */
  fetchDictionaries?: typeof listDictionaries
  /** Test seam — defaults to the real single-dictionary (with entries) fetch. */
  fetchDictionaryDetail?: typeof getDictionary
  /** Test seam — defaults to the real "create dictionary" call. */
  createDictionary?: typeof createDictionaryRequest
  /** Test seam — defaults to the real "add term to dictionary" call. */
  addDictionaryEntry?: typeof createDictionaryEntryRequest
  /** Test seam for the frecency suggestion store — defaults to `localStorage`. */
  hintStorage?: HintStorageLike
}

/**
 * Submit-only upload modal opened from the /transcriptions header. It only sends
 * files (+ optional hint words) for transcription; progress, download, and retry
 * all live on the list itself. On success the parent prepends the new rows.
 */
export function UploadModal({
  open,
  onClose,
  onSubmitted,
  loadStoredToken = readStoredToken,
  submit = submitAudioBatch,
  submitLinks = submitAudioBatchLinks,
  preflight = preflightAudioBatch,
  maxFileMinutes = null,
  maxFileBytes = null,
  currentPlan = null,
  navigateToPricing = (url) => window.location.assign(url),
  probeDuration = probeFileDurationSecs,
  taigiEnabled = false,
  fetchTemplates = fetchNotesTemplates,
  teamContext = false,
  fetchDictionaries = listDictionaries,
  fetchDictionaryDetail = getDictionary,
  createDictionary = createDictionaryRequest,
  addDictionaryEntry = createDictionaryEntryRequest,
  hintStorage,
}: UploadModalProps) {
  const { t } = useTranslation('web')
  const [queue, setQueue] = useState<File[]>([])
  const [sourceMode, setSourceMode] = useState<UploadSourceMode>('files')
  const [linkText, setLinkText] = useState('')
  const parsedLinks = useMemo(() => parseSourceLinks(linkText), [linkText])
  const linkMode = sourceMode === 'links'
  const [errors, setErrors] = useState<string[]>([])
  // 'auto' = default STT; 'taigi' = Breeze ASR-26. Only selectable when taigiEnabled.
  const [language, setLanguage] = useState<AudioBatchLanguage>('auto')
  const [visualContext, setVisualContext] = useState(false)
  const [shareToTeam, setShareToTeam] = useState(false)
  // 整理範本 to run once each transcript lands. `null` = 只要逐字稿: 整理 spends
  // credits, so it has to be asked for. Once the workspace's template list has
  // loaded, the dialog moves to the template the last accepted upload from this
  // workspace used (`remembered-template.ts`).
  const [notesTemplateId, setNotesTemplateId] = useState<number | null>(null)
  // The workspace whose options this dialog is loading or showing: where an
  // accepted upload's template is remembered. Known from the start of each load,
  // so an upload sent before the list arrives is still remembered.
  const templateWorkspaceRef = useRef<string | null>(null)
  // Whether the template on screen is final for this workspace: the remembered
  // one has been applied, the user picked one (the picker works while the list
  // loads, with transcript-only as its one option), or Start was pressed. A
  // list that arrives or is loaded again after that leaves the choice alone.
  const templateSettledRef = useRef(false)
  const chooseTemplate = useCallback((id: number | null) => {
    templateSettledRef.current = true
    setNotesTemplateId(id)
  }, [])
  const [templates, setTemplates] = useState<NotesTemplate[]>([])
  const [templatesFailed, setTemplatesFailed] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  // The queue can render a different picker button. Wait for it to reset so
  // KiaoModal never focuses a queued-state button that is about to unmount.
  const [initialFocusReady, setInitialFocusReady] = useState(false)
  const [limitDialog, setLimitDialog] = useState<
    | {
        reason: 'duration' | 'size'
        max: number
        actual: number
        plan?: string
      }
    | {
        reason: 'credits'
        totalDurationSecs: number
        creditsNeeded: number
        creditsAvailable: number
      }
    | null
  >(null)
  const [saveDialog, setSaveDialog] = useState<{ term: string } | null>(null)
  const [saveTarget, setSaveTarget] = useState<string>(SAVE_TARGET_NEW)
  const [saveName, setSaveName] = useState('')
  const [saveBusy, setSaveBusy] = useState(false)
  const [saveDialogError, setSaveDialogError] = useState<string | null>(null)
  const [saveNotice, setSaveNotice] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const filePickerButtonRef = useRef<HTMLButtonElement | null>(null)

  // Read at Start time: all three are derived from hooks mounted below, which
  // in turn need this hook's `submitting`.
  const startBlockedRef = useRef(false)
  const captureSubmissionRef = useRef<() => SubmissionSnapshot>(() => ({
    options: {},
    recordShipped: () => {},
  }))

  const lifecycle = useUploadLifecycle({
    queue,
    links: linkMode ? parsedLinks.urls : null,
    isBlocked: () => startBlockedRef.current,
    loadStoredToken,
    submit,
    submitLinks,
    preflight,
    probeDuration,
    maxFileMinutes,
    captureSubmission: () => captureSubmissionRef.current(),
    onOverCap: (max, actual, plan) => setLimitDialog({ reason: 'duration', max, actual, plan }),
    onCreditsShort: ({ totalDurationSecs, creditsNeeded, creditsAvailable }) =>
      setLimitDialog({ reason: 'credits', totalDurationSecs, creditsNeeded, creditsAvailable }),
    onValidationErrors: setErrors,
    onSubmitted,
  })
  const {
    submitting,
    submitError,
    setSubmitError,
    start: handleStart,
    cancel: handleCancelUpload,
    beginSession,
    currentSession,
    isCurrentSession,
    abortInFlight,
    reset: resetLifecycle,
  } = lifecycle
  const upload = { progress: lifecycle.progress }

  // The hint cap is derived downstream from this hook's own output, and the
  // workspace loader is declared below it; both are read at call time.
  const hintLimitReachedRef = useRef(false)
  const captureWorkspaceRef = useRef<() => () => boolean>(() => () => true)
  const captureWorkspace = useCallback(() => captureWorkspaceRef.current(), [])

  const dictionary = useUploadDictionaries({
    submitting,
    hintLimitReachedRef,
    currentSession,
    isCurrentSession,
    captureWorkspace,
    onDictionariesRemoved: (removed: Set<number>) =>
      setSaveTarget((prev) => (removed.has(Number(prev)) ? SAVE_TARGET_NEW : prev)),
    fetchDictionaries,
    fetchDictionaryDetail,
  })
  const {
    clear: clearDictionaries,
    loadList: loadDictionaryList,
    resetForOpen: resetDictionariesForOpen,
    dictionaries,
    sources: dictionarySources,
    termsPending: dictionaryTermsPending,
    termsFailed: dictionaryTermsFailed,
    registerCreatedDictionary,
    ensureEntries,
    recordEntryAdded,
  } = dictionary

  const hintTerms = useHintTerms({
    open,
    submitting,
    dictionarySources,
    hintStorage,
  })
  const {
    visibleHints,
    overHintLimit,
    hintLimitReached,
    graduateTerm,
    recordShippedUsage,
    reset: resetHintTerms,
    clearForWorkspaceSwitch: clearHintTermsForWorkspaceSwitch,
  } = hintTerms
  // Declared above the ref assignments below, which close over them: a closure
  // that referenced a later `const` would only be safe while nothing evaluated
  // it during render, and neither typecheck nor lint would catch the day that
  // changed.
  // Links are fetched audio-only, so the on-screen option is a file-mode setting.
  const visualContextEligible = !linkMode && queue.length > 0 && queue.every(isVideoBatchFile)
  const effectiveMaxFileBytes = maxFileBytes ?? UNKNOWN_PLAN_MAX_FILE_BYTES

  hintLimitReachedRef.current = hintLimitReached
  startBlockedRef.current = overHintLimit || dictionaryTermsPending
  captureSubmissionRef.current = () => {
    // Pressing Start settles the template too: a list that arrives while the
    // upload runs must not swap in the remembered one, or a retry after a
    // failure would send (and bill for) a template this upload never had.
    templateSettledRef.current = true
    // Frozen with the payload: the workspace can change while the upload runs.
    // Written whatever the list's state was, so a transcript-only upload sent
    // before the list arrived (or after it failed) still forgets an older choice.
    const templateWorkspace = templateWorkspaceRef.current
    return {
      options: {
        ...(visibleHints.length > 0 ? { enhancementHints: visibleHints } : {}),
        ...(taigiEnabled && language === 'taigi' ? { language } : {}),
        ...(visualContextEligible && visualContext ? { visualContext: true } : {}),
        ...(notesTemplateId ? { notesTemplateId } : {}),
        ...(teamContext && shareToTeam ? { shareToTeam: true } : {}),
      },
      recordShipped: () => {
        recordShippedUsage()
        if (templateWorkspace !== null) writeRememberedTemplate(templateWorkspace, notesTemplateId)
      },
    }
  }

  // Fresh state each time the modal opens, and pull focus inside it for a11y
  // (the parent restores focus to the opener when it unmounts us).
  useEffect(() => {
    beginSession()
    if (!open) {
      setInitialFocusReady(false)
      abortInFlight()
      return
    }
    setQueue([])
    setSourceMode('files')
    setLinkText('')
    setErrors([])
    setLanguage('auto')
    setVisualContext(false)
    setShareToTeam(false)
    setNotesTemplateId(null)
    templateSettledRef.current = false
    setTemplatesFailed(false)
    setIsDragging(false)
    setLimitDialog(null)
    resetDictionariesForOpen()
    resetHintTerms()
    resetLifecycle()
    setSaveDialog(null)
    setSaveNotice(null)
    // A save in flight when the modal closed skips its own `finally` cleanup
    // (stale-session guard) — clear the latch here or saving stays disabled.
    setSaveBusy(false)
    setInitialFocusReady(true)
    return abortInFlight
  }, [abortInFlight, beginSession, open, resetDictionariesForOpen, resetHintTerms, resetLifecycle])

  useEffect(() => {
    if (!visualContextEligible) setVisualContext(false)
  }, [visualContextEligible])

  useEffect(() => {
    if (!teamContext) setShareToTeam(false)
  }, [teamContext])

  const clearWorkspaceOptions = useCallback(() => {
    setTemplates([])
    setTemplatesFailed(false)
    setNotesTemplateId(null)
    templateSettledRef.current = false
    clearDictionaries()
    clearHintTermsForWorkspaceSwitch()
    setSaveDialog(null)
    setSaveTarget(SAVE_TARGET_NEW)
    setSaveName('')
    setSaveBusy(false)
    setSaveDialogError(null)
    setSaveNotice(null)
  }, [clearDictionaries, clearHintTermsForWorkspaceSwitch])

  const loadWorkspaceOptions = useCallback(
    async ({ isCurrent, workspaceKey }: WorkspaceScopedLoadContext) => {
      templateWorkspaceRef.current = workspaceKey
      const loadTemplates = async () => {
        try {
          const body = await fetchTemplates()
          if (!isCurrent()) return
          setTemplates(body.templates)
          setTemplatesFailed(false)
          if (!templateSettledRef.current) {
            templateSettledRef.current = true
            setNotesTemplateId(restoreRememberedTemplate(workspaceKey, body.templates))
          }
        } catch (error) {
          reportHandledException(error, { source: 'app/app/transcriptions/upload-modal.tsx' })
          if (!isCurrent()) return
          console.error('[upload-modal] 整理範本 list failed to load', error)
          setTemplates([])
          setTemplatesFailed(true)
        }
      }

      await Promise.all([loadTemplates(), loadDictionaryList(isCurrent)])
    },
    [fetchTemplates, loadDictionaryList]
  )

  const { reload: reloadWorkspaceOptions, captureWorkspace: captureWorkspaceNow } =
    useWorkspaceScopedLoader({
      enabled: open,
      clear: clearWorkspaceOptions,
      load: loadWorkspaceOptions,
    })
  captureWorkspaceRef.current = captureWorkspaceNow

  const selectionWarning = useUploadSelectionPreflight({
    open,
    queue: linkMode ? NO_FILES : queue,
    visualContext: visualContextEligible && visualContext,
    maxFileMinutes,
    currentPlan,
    loadStoredToken,
    probeDuration,
    preflight,
  })

  useRefreshOnRefocus({ enabled: open && !submitting, onRefocus: dictionary.refresh })

  if (!open) return null

  const addFiles = (incoming: FileList | File[]) => {
    if (submitting) return
    const list = Array.isArray(incoming) ? incoming : Array.from(incoming)
    if (list.length === 0) return
    const { accepted, errors: rejected } = validateAdditions(
      queue.length,
      list,
      queue.reduce((total, file) => total + file.size, 0),
      effectiveMaxFileBytes
    )
    if (accepted.length > 0) setQueue((prev) => [...prev, ...accepted])
    const oversized = rejected.find((error) => error.kind === 'file_too_large')
    if (oversized && currentPlan) {
      setLimitDialog({ reason: 'size', max: oversized.max, actual: oversized.size })
    }
    // The focused plan-limit dialog owns file-size feedback. Other validation
    // failures remain inline and accepted files stay queued.
    setErrors(
      rejected
        .filter((error) => !(error.kind === 'file_too_large' && currentPlan))
        .map((err) => describeValidationError(err, t))
    )
    setSubmitError(null)
  }

  const removeFile = (index: number) => {
    if (submitting) return
    setQueue((prev) => prev.filter((_, i) => i !== index))
  }

  const openSaveDialog = (term: string) => {
    if (submitting) return
    setSaveNotice(null)
    setSaveDialogError(null)
    setSaveName('')
    setSaveTarget(dictionaries.length > 0 ? String(dictionaries[0].id) : SAVE_TARGET_NEW)
    setSaveDialog({ term })
  }

  const handleSaveTerm = async () => {
    if (!saveDialog || saveBusy) return
    const term = saveDialog.term
    // Same stale-session invariant as handleStart/toggleDictionary: a save
    // resolving after the modal closed (and possibly reopened) must not write
    // notices, list entries or suggestion graduations into the new session.
    const session = currentSession()
    setSaveDialogError(null)
    setSaveBusy(true)
    try {
      let dictionaryId: number
      let dictionaryName: string
      /** The dictionary's full term list as this flow knows it, for the append. */
      let knownEntries: DictionaryEntryView[] = []
      if (saveTarget === SAVE_TARGET_NEW) {
        const name = saveName.trim()
        if (!name) {
          setSaveDialogError(t('batchTranscription.saveNeedName'))
          return
        }
        const created = await createDictionary({ name })
        if (!isCurrentSession(session)) return
        dictionaryId = created.id
        dictionaryName = created.name
        registerCreatedDictionary(created)
        // Retarget the dialog at it so a failed entry write below retries INTO
        // this dictionary instead of creating another one.
        setSaveTarget(String(created.id))
      } else {
        dictionaryId = Number(saveTarget)
        dictionaryName = dictionaries.find((entry) => entry.id === dictionaryId)?.name ?? ''
        // The API answers a repeat term with a 422 that carries no machine code
        // worth branching on, so the check happens here: an already-filed term
        // is a benign outcome, not a failed request.
        const entries = await ensureEntries(dictionaryId)
        if (entries === null || !isCurrentSession(session)) return
        knownEntries = entries
        if (entries.some((entry) => entry.term.trim() === term)) {
          setSaveNotice(t('batchTranscription.saveDuplicate', { term, name: dictionaryName }))
          graduateTerm(term)
          setSaveDialog(null)
          return
        }
      }
      const created = await addDictionaryEntry(dictionaryId, { term })
      if (!isCurrentSession(session)) return
      // This local append is now the newest truth for the dictionary: a refocus
      // refresh that read it BEFORE the write must not put the pre-save list
      // back and make the word the user just saved disappear. The LIST
      // generation is deliberately left alone — claiming it would also discard
      // that refresh's removal handling, leaving a deleted dictionary checked
      // and still shipping its terms, which is worse than a count that is one
      // behind until the next catch-up.
      recordEntryAdded(dictionaryId, created, knownEntries)
      setSaveNotice(t('batchTranscription.saveSucceeded', { term, name: dictionaryName }))
      graduateTerm(term)
      setSaveDialog(null)
    } catch (error) {
      reportHandledException(error, { source: 'app/app/transcriptions/upload-modal.tsx' })
      // Log BEFORE the stale-session bail (repo rule: no silent catch).
      console.error('[upload-modal] saving a hint word into a dictionary failed', error)
      if (!isCurrentSession(session)) return
      setSaveDialogError(t('batchTranscription.saveFailed', { term }))
    } finally {
      if (isCurrentSession(session)) setSaveBusy(false)
    }
  }

  const openPicker = () => {
    if (!submitting) fileInputRef.current?.click()
  }

  const changeSourceMode = (mode: UploadSourceMode) => {
    if (submitting || mode === sourceMode) return
    setSourceMode(mode)
    setErrors([])
    setSubmitError(null)
  }

  // Link mode checks what was typed only when Start is pressed: a half-typed
  // URL is not an error yet, and a bad line must never reach the server.
  const startSubmission = () => {
    if (linkMode) {
      if (parsedLinks.invalid.length > 0) {
        setErrors(
          parsedLinks.invalid.map((url) => t('transcriptions.upload.linksInvalid', { url }))
        )
        return
      }
      if (parsedLinks.urls.length > AUDIO_BATCH_MAX_FILES) {
        setErrors([
          t('transcriptions.upload.linksTooMany', {
            max: AUDIO_BATCH_MAX_FILES,
            count: parsedLinks.urls.length,
          }),
        ])
        return
      }
      setErrors([])
    }
    void handleStart()
  }

  const selectedTemplate =
    notesTemplateId === null
      ? null
      : (templates.find((template) => template.id === notesTemplateId) ?? null)
  const queueTotalBytes = queue.reduce((total, file) => total + file.size, 0)
  const filesRemaining = AUDIO_BATCH_MAX_FILES - queue.length
  const itemCount = linkMode ? parsedLinks.urls.length : queue.length
  // Link mode enables Start on any typed line; bad lines are named on press.
  const hasSource = linkMode
    ? parsedLinks.urls.length + parsedLinks.invalid.length > 0
    : queue.length > 0
  const startLabel = dictionaryTermsPending
    ? dictionaryTermsFailed
      ? t('batchTranscription.dictionaryFailedSubmit')
      : t('batchTranscription.dictionaryLoadingSubmit')
    : itemCount > 0
      ? linkMode
        ? t('transcriptions.upload.transcribeLinks', { count: itemCount })
        : t('batchTranscription.transcribeFiles', { count: itemCount })
      : t('batchTranscription.startTranscription')
  const alertMessages = [...errors, ...(submitError ? [submitError] : [])]
  const dialogPlan =
    limitDialog?.reason === 'duration' ? (limitDialog.plan ?? currentPlan) : currentPlan
  const planName =
    dialogPlan === 'creator'
      ? t('pricing.creator')
      : dialogPlan === 'pro'
        ? t('pricing.pro')
        : dialogPlan === 'team'
          ? t('transcriptions.team.team')
          : t('pricing.free')
  const canUpgradeToPro = dialogPlan === 'creator' || dialogPlan === 'free'
  const limitTitle = limitDialog
    ? limitDialog.reason === 'credits'
      ? t('transcriptions.upload.creditsShortTitle')
      : limitDialog.reason === 'duration'
        ? t('transcriptions.upload.planLimitDurationTitle', {
            plan: planName,
            minutes: limitDialog.max,
          })
        : t('transcriptions.upload.planLimitSizeTitle', {
            plan: planName,
            size: formatFileSize(limitDialog.max),
          })
    : ''

  return (
    <>
      <KiaoModal
        open={open}
        // Uploading has exactly one exit, and it is the confirmed Cancel below.
        // Escape, the backdrop and the × all route here, so they must all agree.
        onClose={submitting ? () => {} : onClose}
        title={t('transcriptions.upload.title')}
        closeLabel={t('transcriptions.upload.close')}
        size="wide"
        scrollBody
        initialFocusRef={filePickerButtonRef}
        initialFocusReady={initialFocusReady}
        {...(submitting ? { headAccessory: <UploadLockLabel /> } : {})}
        footer={
          submitting ? (
            <UploadProgressFooter onCancel={() => void handleCancelUpload()} />
          ) : (
            <>
              {linkMode ? (
                parsedLinks.urls.length > 0 ? (
                  <span className="meta num">
                    {t('transcriptions.upload.linksMeta', {
                      count: parsedLinks.urls.length,
                      max: AUDIO_BATCH_MAX_FILES,
                    })}
                  </span>
                ) : null
              ) : queue.length > 0 ? (
                <span className="meta num">
                  {t('transcriptions.upload.queueMeta', {
                    count: queue.length,
                    max: AUDIO_BATCH_MAX_FILES,
                    size: formatFileSize(queueTotalBytes),
                  })}
                </span>
              ) : null}
              <span className="spacer" />
              <Button variant="secondary" onClick={onClose}>
                {t('transcriptions.upload.cancel')}
              </Button>
              <Button
                disabled={!hasSource || overHintLimit || dictionaryTermsPending}
                onClick={startSubmission}
              >
                {startLabel}
              </Button>
            </>
          )
        }
      >
        {submitting ? (
          <UploadProgressPanel
            progress={upload.progress}
            fileNames={linkMode ? parsedLinks.urls : queue.map((file) => file.name)}
          />
        ) : (
          <UploadSettingsBody
            submitting={submitting}
            sourceMode={sourceMode}
            setSourceMode={changeSourceMode}
            linkText={linkText}
            setLinkText={setLinkText}
            offPlatformLinkCount={parsedLinks.offPlatform.length}
            itemCount={itemCount}
            uploadCancelled={upload.progress.phase === 'cancelled'}
            alertMessages={alertMessages}
            selectionWarning={limitDialog ? null : selectionWarning}
            onSelectionAction={navigateToPricing}
            queue={queue}
            addFiles={addFiles}
            removeFile={removeFile}
            openPicker={openPicker}
            fileInputRef={fileInputRef}
            filePickerButtonRef={filePickerButtonRef}
            filesRemaining={filesRemaining}
            effectiveMaxFileBytes={effectiveMaxFileBytes}
            isDragging={isDragging}
            setIsDragging={setIsDragging}
            taigiEnabled={taigiEnabled}
            language={language}
            setLanguage={setLanguage}
            visualContextEligible={visualContextEligible}
            visualContext={visualContext}
            setVisualContext={setVisualContext}
            templates={templates}
            templatesFailed={templatesFailed}
            notesTemplateId={notesTemplateId}
            setNotesTemplateId={chooseTemplate}
            selectedTemplate={selectedTemplate}
            reloadWorkspaceOptions={() => void reloadWorkspaceOptions()}
            teamContext={teamContext}
            shareToTeam={shareToTeam}
            setShareToTeam={setShareToTeam}
            hintTerms={hintTerms}
            dictionary={dictionary}
            openSaveDialog={openSaveDialog}
            saveNotice={saveNotice}
          />
        )}
      </KiaoModal>
      <KiaoModal
        open={limitDialog !== null}
        onClose={() => setLimitDialog(null)}
        title={limitTitle}
        closeLabel={t('transcriptions.upload.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLimitDialog(null)}>
              {t(
                limitDialog?.reason === 'credits'
                  ? 'transcriptions.upload.creditsShortDismiss'
                  : 'transcriptions.upload.planLimitDismiss'
              )}
            </Button>
            {limitDialog?.reason === 'credits' ? (
              <Button
                onClick={() => {
                  setLimitDialog(null)
                  navigateToPricing('/app/account/billing')
                }}
              >
                {t('transcriptions.upload.creditsShortCta')}
              </Button>
            ) : canUpgradeToPro ? (
              <Button
                onClick={() => {
                  setLimitDialog(null)
                  navigateToPricing('/pricing')
                }}
              >
                {t('transcriptions.upload.viewProPlan')}
              </Button>
            ) : null}
          </>
        }
      >
        {limitDialog ? (
          <p>
            {limitDialog.reason === 'credits'
              ? t('transcriptions.upload.creditsShortBody', {
                  minutes: Math.ceil(limitDialog.totalDurationSecs / 60),
                  needed: Math.round(limitDialog.creditsNeeded * 10) / 10,
                  balance: Math.round(limitDialog.creditsAvailable * 10) / 10,
                })
              : limitDialog.reason === 'duration'
                ? t('transcriptions.upload.planLimitDurationBody', {
                    plan: planName,
                    actual: Math.ceil(limitDialog.actual),
                    minutes: limitDialog.max,
                  })
                : t('transcriptions.upload.planLimitSizeBody', {
                    plan: planName,
                    actual: formatFileSize(limitDialog.actual),
                    size: formatFileSize(limitDialog.max),
                  })}
          </p>
        ) : null}
        {limitDialog?.reason !== 'credits' && canUpgradeToPro ? (
          <p>{t('transcriptions.upload.planLimitProHint')}</p>
        ) : null}
      </KiaoModal>
      <SaveTermDialog
        term={saveDialog?.term ?? null}
        dictionaries={dictionaries}
        target={saveTarget}
        onTargetChange={setSaveTarget}
        name={saveName}
        onNameChange={setSaveName}
        busy={saveBusy}
        error={saveDialogError}
        onSubmit={() => void handleSaveTerm()}
        onClose={() => setSaveDialog(null)}
      />
    </>
  )
}
